import { useState, useEffect } from "react";
import { useAuth } from "@/components/auth/AuthProvider";
import { supabase } from "@/integrations/supabase/client";
import { ChevronUp, ChevronDown, Users, Bot, User, Globe } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { getCountryFlag, formatRelativeTime } from "@/lib/botDetection";
import { cn } from "@/lib/utils";

interface VisitorSession {
  id: string;
  session_id: string;
  user_id: string | null;
  user_agent: string | null;
  is_bot: boolean;
  bot_name: string | null;
  current_route: string | null;
  last_seen_at: string;
  country: string | null;
  profiles?: {
    full_name: string | null;
    email: string | null;
  } | null;
}

const REFRESH_INTERVAL = 15 * 1000; // 15 seconds

export const AdminOnlineTracker = () => {
  const { user, isAdmin } = useAuth();
  const [isExpanded, setIsExpanded] = useState(false);
  const [visitors, setVisitors] = useState<VisitorSession[]>([]);
  const [currentUserCountry, setCurrentUserCountry] = useState<string | null>(null);

  // Fetch visitors
  const fetchVisitors = async () => {
    if (!isAdmin) return;

    try {
      const sixtyMinutesAgo = new Date(Date.now() - 60 * 60 * 1000).toISOString();
      
      const { data, error } = await supabase
        .from("visitor_sessions")
        .select(`
          *,
          profiles:user_id (
            full_name,
            email
          )
        `)
        .gte("last_seen_at", sixtyMinutesAgo)
        .order("last_seen_at", { ascending: false });

      if (error) {
        console.error("Error fetching visitors:", error);
        return;
      }

      setVisitors(data || []);
      
      // Find current user's country
      const currentSession = data?.find(v => v.user_id === user?.id);
      if (currentSession?.country) {
        setCurrentUserCountry(currentSession.country);
      }
    } catch (error) {
      console.error("Error in fetchVisitors:", error);
    }
  };

  useEffect(() => {
    if (!isAdmin) return;

    fetchVisitors();
    const intervalId = setInterval(fetchVisitors, REFRESH_INTERVAL);

    return () => clearInterval(intervalId);
  }, [isAdmin, user?.id]);

  if (!isAdmin) return null;

  // Categorize visitors
  const users = visitors.filter(v => v.user_id && !v.is_bot);
  const guests = visitors.filter(v => !v.user_id && !v.is_bot);
  const bots = visitors.filter(v => v.is_bot);

  const VisitorRow = ({ visitor }: { visitor: VisitorSession }) => {
    const isOnline = new Date(visitor.last_seen_at).getTime() > Date.now() - 2 * 60 * 1000;
    const isIdle = !isOnline && new Date(visitor.last_seen_at).getTime() > Date.now() - 10 * 60 * 1000;
    
    const name = visitor.is_bot
      ? visitor.bot_name || "Bot"
      : visitor.profiles?.full_name || visitor.profiles?.email?.split("@")[0] || "Anónimo";
    
    const icon = visitor.is_bot ? (
      <Bot className="h-4 w-4 text-muted-foreground" />
    ) : visitor.user_id ? (
      <User className="h-4 w-4 text-primary" />
    ) : (
      <Globe className="h-4 w-4 text-muted-foreground" />
    );

    return (
      <div className="flex items-center justify-between py-2 px-2 sm:px-3 hover:bg-muted/50 rounded text-xs sm:text-sm gap-1">
        <div className="flex items-center gap-1.5 sm:gap-2 min-w-0 flex-1">
          <span
            className={cn(
              "h-2 w-2 rounded-full flex-shrink-0",
              isOnline ? "bg-green-500" : isIdle ? "bg-yellow-500" : "bg-muted-foreground"
            )}
          />
          {icon}
          <span className="truncate font-medium max-w-[80px] sm:max-w-[120px]">{name}</span>
          <span className="text-sm sm:text-lg flex-shrink-0" title={visitor.country || undefined}>
            {getCountryFlag(visitor.country)}
          </span>
        </div>
        <div className="flex items-center gap-1 sm:gap-3 text-muted-foreground text-[10px] sm:text-xs flex-shrink-0">
          <span className="truncate max-w-[60px] sm:max-w-[120px]" title={visitor.current_route || undefined}>
            {visitor.current_route || "/"}
          </span>
          <span className="whitespace-nowrap">{formatRelativeTime(visitor.last_seen_at)}</span>
        </div>
      </div>
    );
  };

  const currentUserName = user?.user_metadata?.full_name || user?.email?.split("@")[0] || "Admin";

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-card border-t shadow-lg">
      {/* Collapsed Header */}
      <Button
        variant="ghost"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full h-9 sm:h-10 flex items-center justify-between px-2 sm:px-4 rounded-none hover:bg-muted/50"
      >
        <div className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm overflow-hidden">
          <span className="h-2 w-2 rounded-full bg-green-500 flex-shrink-0" />
          <span className="font-medium truncate">
            <span className="hidden xs:inline">Activo: </span>
            {users.length}u {guests.length}i {bots.length}b
          </span>
          <span className="text-muted-foreground hidden md:inline truncate">
            • Tú: {currentUserName} ({currentUserCountry || "?"})
          </span>
        </div>
        {isExpanded ? <ChevronDown className="h-4 w-4 flex-shrink-0" /> : <ChevronUp className="h-4 w-4 flex-shrink-0" />}
      </Button>

      {/* Expanded Panel */}
      {isExpanded && (
        <div className="border-t">
          <Tabs defaultValue="todos" className="w-full">
            <TabsList className="w-full grid grid-cols-4 rounded-none border-b bg-transparent h-9 sm:h-10 px-1 sm:px-4 gap-0.5">
              <TabsTrigger value="todos" className="data-[state=active]:bg-muted text-[10px] sm:text-sm px-1 sm:px-3">
                <span className="hidden sm:inline">Todos </span>({visitors.length})
              </TabsTrigger>
              <TabsTrigger value="usuarios" className="data-[state=active]:bg-muted text-[10px] sm:text-sm px-1 sm:px-3">
                <Users className="h-3 w-3 sm:mr-1" />
                <span className="hidden sm:inline">Usuarios </span>({users.length})
              </TabsTrigger>
              <TabsTrigger value="invitados" className="data-[state=active]:bg-muted text-[10px] sm:text-sm px-1 sm:px-3">
                <Globe className="h-3 w-3 sm:mr-1" />
                <span className="hidden sm:inline">Invitados </span>({guests.length})
              </TabsTrigger>
              <TabsTrigger value="bots" className="data-[state=active]:bg-muted text-[10px] sm:text-sm px-1 sm:px-3">
                <Bot className="h-3 w-3 sm:mr-1" />
                <span className="hidden sm:inline">Bots </span>({bots.length})
              </TabsTrigger>
            </TabsList>

            <ScrollArea className="h-[150px] sm:h-[200px]">
              <TabsContent value="todos" className="m-0 p-2">
                {visitors.length === 0 ? (
                  <p className="text-center text-muted-foreground py-4">Sin actividad</p>
                ) : (
                  visitors.map((visitor) => (
                    <VisitorRow key={visitor.id} visitor={visitor} />
                  ))
                )}
              </TabsContent>

              <TabsContent value="usuarios" className="m-0 p-2">
                {users.length === 0 ? (
                  <p className="text-center text-muted-foreground py-4">Sin usuarios activos</p>
                ) : (
                  users.map((visitor) => (
                    <VisitorRow key={visitor.id} visitor={visitor} />
                  ))
                )}
              </TabsContent>

              <TabsContent value="invitados" className="m-0 p-2">
                {guests.length === 0 ? (
                  <p className="text-center text-muted-foreground py-4">Sin invitados activos</p>
                ) : (
                  guests.map((visitor) => (
                    <VisitorRow key={visitor.id} visitor={visitor} />
                  ))
                )}
              </TabsContent>

              <TabsContent value="bots" className="m-0 p-2">
                {bots.length === 0 ? (
                  <p className="text-center text-muted-foreground py-4">Sin bots activos</p>
                ) : (
                  bots.map((visitor) => (
                    <VisitorRow key={visitor.id} visitor={visitor} />
                  ))
                )}
              </TabsContent>
            </ScrollArea>
          </Tabs>
        </div>
      )}
    </div>
  );
};
